import { Link } from "react-router-dom";
import { ArrowRight, Scale, Users, Heart, Building2, Award, FileText, Mail, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import NewsletterSignup from "@/components/NewsletterSignup";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_66258ad9-e000-456c-bbb1-9b3da697cdba/artifacts/pl46n86d_New%20SCAH%20Logo.png";

const HomePage = () => {
  const stats = [
  { number: "5", label: "Legislative Victories \nlead and galvanized by our organization", icon: Scale },
  { number: "10K+", label: "Texans Impacted", icon: Users },
  { number: "10+", label: "Years of Advocacy", icon: Award },
  { number: "20+", label: "Partner Organizations", icon: Building2 }];


  const legislativeHighlights = [
  {
    bill: "HB 3405",
    title: "Sickle Cell Task Force",
    year: "2019",
    description: "Established the Texas Sickle Cell Task Force"
  },
  {
    bill: "HB 2576",
    title: "Opioid Exemption",
    year: "2019",
    description: "Critical exemptions for pain management access"
  },
  {
    bill: "HB 3394",
    title: "Awareness License Plates",
    year: "2021",
    description: "Raising awareness across Texas roadways"
  }];


  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1739379958174-b363d1e78e1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxIb3VzdG9uJTIwc2t5bGluZSUyMG1vZGVybiUyMHN1bnNldHxlbnwwfHx8fDE3NzAxNDM5ODJ8MA&ixlib=rb-4.1.0&q=85')`
          }}>

          <div className="absolute inset-0 bg-gradient-to-r from-slate-50/95 via-slate-50/85 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={LOGO_URL}
                alt="Sickle Cell Association"
                className="h-20 md:h-24 w-auto drop-shadow-lg" />

              <div>
                <p className="text-red-600 font-bold tracking-widest uppercase text-sm">
                  Sickle Cell Association
                </p>
              </div>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.1] mb-6">
              Advocating for a{" "}
              <span className="text-red-600 italic">Pain-Free</span>{" "}
              Future in Texas
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-xl mb-8">
              Leading the fight for legislative change, patient advocacy, and community support for those affected by sickle cell disease.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/legislative-victories">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 font-serif italic text-lg btn-primary">
                  Our Victories
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/donate">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 font-sans font-bold tracking-wide uppercase text-sm btn-accent">
                  <Heart className="mr-2 w-5 h-5" />
                  Donate Now
                </Button>
              </Link>
            </div>
          </div>

          {/* Glass Card with Stats */}
          <div className="lg:col-span-5 hidden lg:block">
            <div className="glass p-8 rounded-sm animate-slide-up">
              <div className="flex items-center gap-3 mb-6">
                <Scale className="w-8 h-8 text-amber-700" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900">Legislative Impact</h3>
              </div>
              <div className="space-y-4">
                {legislativeHighlights.map((item, index) =>
                <div key={item.bill} className="flex items-start gap-4 p-4 bg-slate-50/80 rounded-sm animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="bg-amber-100 text-amber-800 font-bold text-xs px-3 py-1 rounded">
                      {item.bill}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-500">{item.year} • {item.description}</p>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/legislative-victories" className="inline-flex items-center text-teal-700 font-medium text-sm mt-4 hover:text-teal-800">
                View all victories <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div
              key={stat.label}
              className="text-center animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}>

                <stat.icon className="w-8 h-8 text-amber-500 mx-auto mb-4" />
                <p className="md:text-5xl !font-serif !font-bold !text-4xl mb-2 text-white">{stat.number}</p>
                <p className="uppercase !font-sans !text-sm text-slate-400">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1631558554184-319c88f4f8a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwQW1lcmljYW4lMjBkb2N0b3IlMjB0YWxraW5nJTIwdG8lMjBwYXRpZW50JTIwY29tcGFzc2lvbmF0ZXxlbnwwfHx8fDE3NzAxNDM5NzR8MA&ixlib=rb-4.1.0&q=85"
                alt="Compassionate medical care"
                className="w-full h-[500px] object-cover rounded-sm shadow-lg" />

            </div>
            <div className="order-1 lg:order-2">
              <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
                Our Mission
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                Empowering Lives Through <span className="italic text-teal-700">Advocacy</span>
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                The Sickle Cell Association is dedicated to improving the quality of life for individuals and families affected by sickle cell disease through patient advocacy, policy reform, and community education.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">As a proud partner agency for sickle cell disease for the City of Houston and the Houston Health Department, we work tirelessly to ensure that our community's voices are heard in the halls of the Texas Legislature.

              </p>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 px-8 py-3 font-sans font-bold uppercase text-sm hover:bg-slate-50">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 bg-white" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Trusted Partnerships
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              Working Together for Change
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* City of Houston */}
            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-8">
                <Building2 className="w-12 h-12 text-teal-700 mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">City of Houston</h3>
                <p className="text-slate-600 leading-relaxed">
                  As a partner agency for the City of Houston, we collaborate on public health initiatives and community outreach programs.
                </p>
              </CardContent>
            </Card>

            {/* Houston Health Department */}
            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-8">
                <Heart className="w-12 h-12 text-amber-700 mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Houston Health Department</h3>
                <p className="text-slate-600 leading-relaxed">
                  Working alongside the Houston Health Department to advance sickle cell awareness and improve healthcare access.
                </p>
              </CardContent>
            </Card>

            {/* Legislative Partners */}
            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-8">
                <FileText className="w-12 h-12 text-slate-700 mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Texas Legislature</h3>
                <p className="text-slate-600 leading-relaxed">
                  Partnering with legislators like Rep. Jarvis Johnson, Senator Borris Miles, and Rep. Lauren Simmons to champion landmark sickle cell legislation.
                </p>
              </CardContent>
            </Card>

            {/* Houston Area Hospitals and Clinics */}
            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-8">
                <Stethoscope className="w-12 h-12 text-teal-600 mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Houston Area Hospitals & Clinics</h3>
                <p className="text-slate-600 leading-relaxed">
                  Collaborating with leading healthcare institutions across Greater Houston to ensure comprehensive care for sickle cell patients.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1513023001678-6927b70dc4a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxUZXhhcyUyMFN0YXRlJTIwQ2FwaXRvbCUyMGJ1aWxkaW5nJTIwaW50ZXJpb3IlMjBsZWdpc2xhdGl2ZXxlbnwwfHx8fDE3NzAxNDM5Nzd8MA&ixlib=rb-4.1.0&q=85')`
        }}
        data-testid="cta-section">

        <div className="absolute inset-0 bg-slate-900/85"></div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-400 font-medium tracking-widest uppercase text-sm mb-4">
            Join Our Mission
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Help Us Continue the Fight
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Your support enables us to advocate for life-changing legislation, provide patient support services, and raise awareness about sickle cell disease.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-amber-700 hover:bg-amber-600 text-white px-10 py-6 font-sans font-bold tracking-wide uppercase text-sm btn-accent">
                <Heart className="mr-2 w-5 h-5" />
                Make a Donation
              </Button>
            </Link>
            <Link to="/members">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 font-sans font-bold uppercase text-sm">
                <Users className="mr-2 w-5 h-5" />
                Become a Member
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="py-20 bg-slate-50" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-6">
            <Mail className="w-8 h-8 text-amber-700" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            Stay Informed
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest updates on legislative victories, upcoming events, and ways to support our mission.
          </p>
          <div className="max-w-xl mx-auto">
            <NewsletterSignup />
          </div>
        </div>
      </section>
    </div>);

};

export default HomePage;